import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LoadableData } from 'types';
import {
  VocabularyTestBlock,
  VocabularyTestHistory,
  VocabularyTestHistoryPage,
  StartVocabularyTestRequest,
  VocabularyTestTopic,
} from 'app/types';

export interface VocabularyTestHistoryGetPayload {
  page?: number;
  size?: number;
}

interface Slice {
  vocabularyTestHistory: LoadableData<Array<VocabularyTestHistory>> & {
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
  };
  vocabularyTestInProgress: LoadableData<Array<VocabularyTestBlock>>;
  vocabularyTestTopics: LoadableData<Array<VocabularyTestTopic>>;
  vocabularyTestResult: LoadableData<VocabularyTestHistory | null>;
  completedTestResult: VocabularyTestHistory | null;
  addLearnedWordsLoading: boolean;
  deleteTestHistorySuccessCount: number;
  learnedWordsAdded: boolean;
}

export type VocabularyTestSlice = Slice;

const getInitialState = (): Slice => ({
  vocabularyTestHistory: {
    data: [],
    loading: true,
    number: 0,
    size: 5,
    totalElements: 0,
    totalPages: 0,
  },
  vocabularyTestInProgress: { data: [], loading: false },
  vocabularyTestTopics: { data: [], loading: false },
  vocabularyTestResult: { data: null, loading: false },
  completedTestResult: null,
  addLearnedWordsLoading: false,
  deleteTestHistorySuccessCount: 0,
  learnedWordsAdded: false,
});

const vocabularyTestSlice = createSlice({
  name: 'vocabularyTestSlice',
  initialState: getInitialState(),
  reducers: {
    vocabularyTestHistoryGet: (
      store,
      _action: PayloadAction<VocabularyTestHistoryGetPayload | undefined>,
    ) => {
      store.vocabularyTestHistory.loading = true;
    },
    startTesting: (
      store,
      _action: PayloadAction<StartVocabularyTestRequest | undefined>,
    ) => {
      store.vocabularyTestHistory.loading = true;
      // Mark the in-progress slot as loading too: the saga will POST /current
      // right after this. Without it, the runner mounts mid-flight, sees
      // `loading=false` (initial), and would spawn a duplicate POST /current.
      store.vocabularyTestInProgress.loading = true;
      store.vocabularyTestTopics.data = [];
      store.completedTestResult = null;
      store.learnedWordsAdded = false;
    },
    vocabularyTestHistorySet: (
      store,
      { payload }: PayloadAction<VocabularyTestHistoryPage>,
    ) => {
      store.vocabularyTestHistory.loading = false;
      store.vocabularyTestHistory.data = payload.content;
      store.vocabularyTestHistory.number = payload.number;
      store.vocabularyTestHistory.size = payload.size;
      store.vocabularyTestHistory.totalElements = payload.totalElements;
      store.vocabularyTestHistory.totalPages = payload.totalPages;
    },
    vocabularyTestInProgressGet: (store) => {
      store.vocabularyTestInProgress.loading = true;
      store.vocabularyTestTopics.loading = true;
    },
    vocabularyTestInProgressSet: (
      store,
      { payload }: PayloadAction<Slice['vocabularyTestInProgress']['data']>,
    ) => {
      store.vocabularyTestInProgress.loading = false;
      store.vocabularyTestHistory.loading = false;
      store.vocabularyTestInProgress.data = payload;
    },
    vocabularyTestTopicsSet: (
      store,
      { payload }: PayloadAction<Slice['vocabularyTestTopics']['data']>,
    ) => {
      store.vocabularyTestTopics.loading = false;
      store.vocabularyTestTopics.data = payload;
    },
    vocabularyTestTopicsFail: (store) => {
      store.vocabularyTestTopics.loading = false;
    },
    saveAndGetNext: (store, _action: PayloadAction<VocabularyTestBlock[]>) => {
      store.vocabularyTestInProgress.loading = true;
      store.vocabularyTestTopics.loading = true;
    },
    getPreviousWords: (store) => {
      store.vocabularyTestInProgress.loading = true;
      store.vocabularyTestTopics.loading = true;
    },
    finishTest: (store, _action: PayloadAction<VocabularyTestBlock[]>) => {
      store.vocabularyTestHistory.loading = true;
      store.vocabularyTestInProgress.loading = true;
      store.completedTestResult = null;
      store.learnedWordsAdded = false;
    },
    completedTestResultSet: (
      store,
      action: PayloadAction<VocabularyTestHistory>,
    ) => {
      store.completedTestResult = action.payload;
      store.vocabularyTestInProgress.loading = false;
      store.vocabularyTestHistory.loading = false;
      store.vocabularyTestTopics.loading = false;
    },
    completedTestResultClose: (store) => {
      store.completedTestResult = null;
      store.learnedWordsAdded = false;
      store.addLearnedWordsLoading = false;
    },
    vocabularyTestResultGet: (store, _action: PayloadAction<string>) => {
      store.vocabularyTestResult.loading = true;
      store.vocabularyTestResult.data = null;
    },
    vocabularyTestResultSet: (
      store,
      action: PayloadAction<VocabularyTestHistory>,
    ) => {
      store.vocabularyTestResult.loading = false;
      store.vocabularyTestResult.data = action.payload;
    },
    vocabularyTestResultFail: (store) => {
      store.vocabularyTestResult.loading = false;
      store.vocabularyTestResult.data = null;
    },
    addLearnedWordsToVocabulary: (store, _action: PayloadAction<string>) => {
      store.addLearnedWordsLoading = true;
    },
    addLearnedWordsToVocabularySuccess: (store) => {
      store.addLearnedWordsLoading = false;
      store.learnedWordsAdded = true;
    },
    addLearnedWordsToVocabularyFail: (store) => {
      store.addLearnedWordsLoading = false;
    },
    finishTestFail: (store) => {
      store.vocabularyTestHistory.loading = false;
      store.vocabularyTestInProgress.loading = false;
      store.vocabularyTestTopics.loading = false;
    },
    deleteTestHistory: (_store, _action: PayloadAction<string>) => {},
    deleteTestHistorySuccess: (store) => {
      store.deleteTestHistorySuccessCount += 1;
    },
  },
  selectors: {
    activeTestId: (store) => store.vocabularyTestHistory?.data[0]?.id || '',
  },
});

export default vocabularyTestSlice;
export const vocabularyTestReducer = vocabularyTestSlice.reducer;
export const vocabularyTestActions = vocabularyTestSlice.actions;
export const vocabularyTestSelectors = vocabularyTestSlice.selectors;
